import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import { tenorApiKey } from '@/config';
import menuItems from '@/components/inbox/menu-items';

export const state = () => ({
    search: '',
    items: cloneDeep(menuItems),
    source: null,
    flowModeTimeout: null,
    isFlowModeEnabled: false,
    memberDrilldown: null,
    statusFormModalItem: null,
    isFlowModePausedModalOpened: false,
    allCategories: menuItems.flatMap(item => item.categories || [])
});

export const getters = {
    search: state => state.search,
    source: state => state.source,
    memberDrilldown: state => state.memberDrilldown,
    statusFormModalItem: state => state.statusFormModalItem,
    isFlowModeEnabled: state => state.isFlowModeEnabled,
    isFlowModePausedModalOpened: state => state.isFlowModePausedModalOpened,
    items: (state, getters, rootState, rootGetters) => {
        const items = state.items.filter(network => {
            const isAnyCategory = network.categories.some(category => {
                return (
                    category.items.some(item => item.name.toLowerCase().includes(getters.search.toLowerCase())) ||
                    ['Custom Inboxes', 'Labels'].includes(network.text)
                );
            });

            return rootGetters['auth/isSysAdmin'] || isAnyCategory;
        });

        return items.filter(item => !item.roles || item.roles.includes(rootGetters['auth/loggedUserRole']));
    },
    getItemsByCategoryId: state => categoryId => {
        return state.allCategories.find(category => category && category.id === categoryId)?.items;
    }
};

export const mutations = {
    SET_SOURCE(state, source) {
        state.source = source;
    },

    SET_SEARCH(state, search) {
        state.search = search;
    },

    ADD_ITEM_TO_CATEGORY(state, item) {
        const category = state.allCategories.find(category => category.id === item.categoryId);

        if (category) {
            // @tmp
            const id = `${item.categoryId}-${(Math.random() + 1).toString(36).substring(7)}`;

            category.items.push({ ...item, id });
        }
    },

    ADD_SUB_ITEM_TO_CATEGORY(state, subItem) {
        const category = state.allCategories.find(category => category.id === subItem.categoryId);

        if (category) {
            const item = category.items.find(item => item.id === subItem.itemId);

            if (item) {
                // @tmp
                const id = `${item.categoryId}-${subItem.itemId}-${(Math.random() + 1).toString(36).substring(7)}`;

                item.subItems.push({ ...subItem, id });
            }
        }
    },

    UPDATE_CATEGORY_ITEM(state, item) {
        const categoryIndex = state.allCategories.findIndex(category => category && category.id === item.categoryId);

        if (~categoryIndex) {
            const itemIndex = state.allCategories[categoryIndex].items.findIndex(
                categoryItem => categoryItem && categoryItem.id === item.id
            );

            if (~itemIndex) {
                state.allCategories[categoryIndex].items.splice(itemIndex, 1, item);
            }
        }
    },

    UPDATE_CATEGORY_SUB_ITEM(state, subItem) {
        const categoryIndex = state.allCategories.findIndex(category => category && category.id === subItem.categoryId);

        if (~categoryIndex) {
            const itemIndex = state.allCategories[categoryIndex].items.findIndex(
                categoryItem => categoryItem && categoryItem.id === subItem.itemId
            );

            if (~itemIndex) {
                const subItemIndex = state.allCategories[categoryIndex].items[itemIndex].subItems.findIndex(
                    categorySubItem => categorySubItem && categorySubItem.id === subItem.id
                );

                if (~subItemIndex) {
                    state.allCategories[categoryIndex].items[itemIndex].subItems.splice(subItemIndex, 1, subItem);
                }
            }
        }
    },

    DELETE_CATEGORY_ITEM(state, { id, categoryId }) {
        state.allCategories = state.allCategories.map(category => {
            if (category.id !== categoryId) {
                return category;
            }

            return {
                ...category,
                items: category.items.filter(item => item.id !== id)
            };
        });
    },

    DELETE_CATEGORY_SUB_ITEM(state, { id, itemId, categoryId }) {
        state.allCategories = state.allCategories.map(category => {
            if (category.id !== categoryId) {
                return category;
            }

            const items = category.items.map(item => {
                if (item.id !== itemId) {
                    return item;
                }

                return {
                    ...item,
                    subItems: item.subItems.filter(subItem => subItem.id !== id)
                };
            });

            return {
                ...category,
                items: items.filter(item => item.subItems?.length > 0)
            };
        });
    },

    SET_IS_FLOW_MODE_ENABLED(state, val) {
        state.isFlowModeEnabled = val;

        if (!val) {
            clearTimeout(state.flowModeTimeout);

            return;
        }

        const flowModeTime = 1000 * 30;

        state.flowModeTimeout = setTimeout(() => {
            state.isFlowModeEnabled = false;
            state.isFlowModePausedModalOpened = true;
        }, flowModeTime);
    },

    SET_IS_FLOW_MODE_PAUSED_MODAL_OPENED(state, val) {
        state.isFlowModePausedModalOpened = val;
    },

    SET_MEMBER_DRILLDOWN(state, member) {
        state.memberDrilldown = member;
    },

    SET_STATUS_FORM_MODAL_ITEM(state, item) {
        state.statusFormModalItem = item;
    }
};

export const actions = {
    setMemberDrilldown({ commit }, member) {
        commit('SET_MEMBER_DRILLDOWN', member);
    },

    setStatusFormModalItem({ commit }, item) {
        commit('SET_STATUS_FORM_MODAL_ITEM', item);
    },

    setSource({ commit }, source) {
        commit('SET_SOURCE', source);
        commit('SET_IS_FLOW_MODE_ENABLED', false);
    },

    setSearch({ commit }, search) {
        commit('SET_SEARCH', search);
    },

    addItemToCategory({ commit, rootGetters }, item) {
        // TODO:
        // !: @TMP: (shouldn't pass loggedUser!!)
        const loggedUser = rootGetters['auth/loggedUser'];

        item.author = cloneDeep(loggedUser);

        commit('ADD_ITEM_TO_CATEGORY', item);
    },

    addSubItemToCategory({ commit, rootGetters }, subItem) {
        // TODO:
        // !: @TMP: (shouldn't pass loggedUser!!)
        const loggedUser = rootGetters['auth/loggedUser'];

        subItem.author = cloneDeep(loggedUser);

        commit('ADD_SUB_ITEM_TO_CATEGORY', subItem);
    },

    updateCategoryItem({ commit }, item) {
        commit('UPDATE_CATEGORY_ITEM', item);
        // @TODO set source if updating item is the selected one
    },

    updateCategorySubItem({ commit }, subItem) {
        commit('UPDATE_CATEGORY_SUB_ITEM', subItem);
    },

    deleteCategoryItem({ commit, getters: { source } }, { id, categoryId }) {
        commit('DELETE_CATEGORY_ITEM', { id, categoryId });

        if (source?.item.id === id) {
            commit('SET_SOURCE', null);
            commit('SET_IS_FLOW_MODE_ENABLED', false);
        }
    },

    deleteCategorySubItem({ commit }, { id, itemId, categoryId }) {
        commit('DELETE_CATEGORY_SUB_ITEM', { id, itemId, categoryId });
    },

    toggleFlowMode({ commit, getters: { isFlowModeEnabled } }) {
        commit('SET_IS_FLOW_MODE_ENABLED', !isFlowModeEnabled);
    },

    closeFlowModePausedModal({ commit }) {
        commit('SET_IS_FLOW_MODE_PAUSED_MODAL_OPENED', false);
    },

    enableFlowMode({ commit, dispatch }) {
        commit('SET_IS_FLOW_MODE_ENABLED', true);
        dispatch('closeFlowModePausedModal');
    },

    getGifs(vuexContext, search) {
        return axios.$get(`https://g.tenor.com/v1/search?q=${search}&key=${tenorApiKey}&limit=8`, {
            withCredentials: false
        });
    }
};
